import { DIRECTION_LETTERS, DIRECTION_BEARINGS } from './courseConstants.js';

/**
 * Parse shortform mark sequence string into structured data.
 *
 * Two modes:
 * 1. Single-char mode (no separators): "ApBsCp" → A(P) B(S) C(P)
 * 2. Multi-char mode (any separator — space, comma, dash, slash, arrow, …):
 *    "MUG(p) KSH(s) BRO" / "MUG-KSH-BRO" / "MUG → KSH → BRO" → MUG(P) KSH(S) BRO(P)
 *
 * Format (single-char):
 * - MarkID: single uppercase letter or digit
 * - Rounding: 'p' = port, 's' = starboard (default: port if omitted)
 *
 * Format (multi-char):
 * - Mark IDs separated by spaces or commas
 * - Optional (p) or (s) suffix for rounding
 *
 * Examples:
 *   "ApBsCp"       → [{id:'A',rounding:'port'}, {id:'B',rounding:'starboard'}, {id:'C',rounding:'port'}]
 *   "ABC"           → [{id:'A',rounding:'port'}, {id:'B',rounding:'port'}, {id:'C',rounding:'port'}]
 *   "MUG KSH BRO"  → [{id:'MUG',rounding:'port'}, {id:'KSH',rounding:'port'}, {id:'BRO',rounding:'port'}]
 *   "MUG(p) KSH(s)" → [{id:'MUG',rounding:'port'}, {id:'KSH',rounding:'starboard'}]
 */
// Characters a user might place between marks in a sequence (Chris, ra-npo7:
// "support spaces, commas and other separators"). A mark token is alphanumeric
// (+ an optional (p)/(s) rounding suffix), so none of these can be part of one —
// so "A,B", "A B", "A-B", "A / B", "A > B", "A→B", "A;B" all tokenise the same.
// The trailing '-' in the class is a literal hyphen, not a range.
export const MARK_SEPARATORS = /[\s,;/\\|>·•–—→-]+/;
const TRAILING_MARK_SEPARATOR = /[\s,;/\\|>·•–—→-]$/;

/** True if the input contains any mark separator (→ tokenise as a sequence). */
export function hasMarkSeparator(input) {
  return MARK_SEPARATORS.test(input || '');
}

/** True if the input ENDS with a separator (used to preserve a trailing space so
 *  the next mark token can be typed). */
export function endsWithMarkSeparator(input) {
  return TRAILING_MARK_SEPARATOR.test(input || '');
}

/** Split an input into raw mark tokens on any separator run. */
export function splitMarkTokens(input) {
  return (input || '').trim().split(MARK_SEPARATORS).filter(Boolean);
}

export function parseMarkSequence(input, defaultRounding = 'port') {
  const s = (input || '').trim();
  if (!s) return [];

  // Multi-char mode: any separator (space/comma/dash/slash/arrow/…) → split.
  if (hasMarkSeparator(s)) {
    return splitMarkTokens(s).map((token) => {
      let rounding = defaultRounding;
      let id = token;
      // Check for (p)/(s) suffix
      const parenMatch = id.match(/^(.+)\(([ps])\)$/i);
      if (parenMatch) {
        id = parenMatch[1];
        rounding = parenMatch[2].toLowerCase() === 's' ? 'starboard' : 'port';
      } else if (id.length > 1) {
        // Check for trailing lowercase p/s as rounding hint
        const last = id[id.length - 1];
        if (last === 'p' && id.length > 1) { rounding = 'port'; id = id.slice(0, -1); }
        else if (last === 's' && id.length > 1) { rounding = 'starboard'; id = id.slice(0, -1); }
      }
      return { id, rounding };
    });
  }

  // Single-char mode (original behavior)
  const result = [];
  let i = 0;

  while (i < s.length) {
    const ch = s[i];
    if ((ch >= 'A' && ch <= 'Z') || (ch >= '0' && ch <= '9')) {
      let rounding = defaultRounding;
      if (i + 1 < s.length) {
        const next = s[i + 1];
        if (next === 'p') { rounding = 'port'; i += 2; }
        else if (next === 's') { rounding = 'starboard'; i += 2; }
        else { i += 1; }
      } else {
        i += 1;
      }
      result.push({ id: ch, rounding });
    } else {
      i += 1; // skip invalid chars
    }
  }
  return result;
}

/**
 * Serialize mark sequence back to shortform string.
 */
export function serializeMarkSequence(marks) {
  return marks
    .map((m) => m.id + (m.rounding === 'starboard' ? 's' : 'p'))
    .join('');
}

/**
 * Parse a bulk entry line into a course card object.
 *
 * Supported formats:
 *   "A1 ApBsCp 2.5"      (no-dash, primary)
 *   "A-1 ApBsCp 2.5"     (dash, backwards compat)
 *   "A1 ApBsCp"           (no distance — auto-calculated)
 *   "A,1,ApBsCp,2.5"     (comma-separated)
 *   "A,1,ApBsCp"          (comma, no distance)
 *
 * Distance is optional. When omitted, distance is null (auto-calculated).
 */
export function parseBulkLine(line) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('#')) return null;

  // Try comma-separated first
  let parts;
  if (trimmed.includes(',')) {
    parts = trimmed.split(',').map((p) => p.trim());
  } else {
    // Try combined direction-number format (A1 or A-1) with optional distance
    const match = trimmed.match(
      /^([A-Ra-r])-?(\d)\s+([A-Za-z0-9]+)(?:\s+([\d.]+)\s*(?:nm)?)?$/
    );
    if (match) {
      parts = match[4] != null
        ? [match[1], match[2], match[3], match[4]]
        : [match[1], match[2], match[3]];
    } else {
      parts = trimmed.split(/\s+/);
    }
  }

  if (parts.length < 2) return null;

  // Extract direction code, course number, mark sequence, and optional distance
  let dirCode, courseNum, seqStr, distStr;

  if (parts[0].includes('-')) {
    // "A-1" format
    const [d, n] = parts[0].split('-');
    dirCode = d;
    courseNum = n;
    seqStr = parts[1];
    distStr = parts[2];
  } else {
    // Check for combined "A1" format (letter followed by digit)
    const combined = parts[0].match(/^([A-Ra-r])(\d)$/);
    if (combined) {
      dirCode = combined[1];
      courseNum = combined[2];
      seqStr = parts[1];
      distStr = parts[2];
    } else if (parts.length >= 3) {
      // "A 1 ApBsCp [2.5]" — separate fields
      dirCode = parts[0];
      courseNum = parts[1];
      seqStr = parts[2];
      distStr = parts[3];
    } else {
      return null;
    }
  }

  const dirUpper = dirCode.toUpperCase();
  if (!DIRECTION_LETTERS.includes(dirUpper)) return null;

  const num = parseInt(courseNum, 10);
  if (isNaN(num) || num < 1 || num > 8) return null;

  const marks = parseMarkSequence(seqStr);
  if (marks.length === 0) return null;

  let distance = null;
  if (distStr != null) {
    const parsed = parseFloat(distStr.replace(/nm$/i, ''));
    distance = isNaN(parsed) ? 0 : parsed;
  }

  return {
    id: crypto.randomUUID(),
    directionCode: dirUpper,
    courseNumber: num,
    markIds: marks.map((m) => m.id),
    markDirections: marks.map((m) => m.rounding),
    bearing: Math.round(DIRECTION_BEARINGS[dirUpper]),
    distance,
  };
}

/**
 * Parse multiple lines of bulk entry text.
 */
export function parseBulkText(text) {
  return text
    .split('\n')
    .map(parseBulkLine)
    .filter(Boolean);
}

/**
 * Parse freeform text into courses, using LLM for ambiguous input.
 * Delegates to llmParser.js for the heavy lifting.
 */
export async function parseFreeformText(text, options = {}) {
  const { parseFreeformText: llmParse } = await import('./llmParser.js');
  return llmParse(text, options);
}
